import PointHistoryList from "../organisms/PointHistoryList";
import PointHistoryPointCard from "../organisms/PointHistoryPointCard";
import BaseHead from "../templates/BaseHead";
import STUtils from "../../assets/styles/utility.module.scss";
import { getSubdomain } from "../../utils/getSubdomain";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TAppPointHistory } from "../../types/TAppPointHistory";
import { TAppRankSummary } from "../../types/TAppRankSummary";
import {
  fetchPointHistories,
  TApiPointHisotriesResult,
} from "../../utils/api/fetchPointHistories";
import TApiResult from "../../types/api/TApiResult";
import { routes } from "../../router/Router";
import { postMemberAccessLog } from "../../utils/api/postMemberAccessLog";
import { ACCESS_LOG_ACTION } from "../../types/TAppAccessLogAction";
import { SiteContext } from "../../providers/SiteProvider";
import BottomNavigationBar from "../organisms/BottomNavigationBar";

const POINT_HISTORY_LIMIT = 20;
const years = [...Array(3)].map((_, i) => `${new Date().getFullYear() - i}`);

const Presenter: React.FC = () => {
  const subdomain = getSubdomain(window.location.hostname);
  const [year, setYear] = useState(years[0]);

  const [pointHistories, setPointHistories] = useState<TAppPointHistory[]>([]);
  const [rankSummary, setRankSummary] = useState<TAppRankSummary>();
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const site = useContext(SiteContext);

  const getStatus = (code: number, isCancel: boolean) => {
    if (isCancel) return "取消済";

    switch (code) {
      case 1:
        return "申請中";
      case 2:
        return "承認済";
      case 9:
        return "却下";
      default:
        return "";
    }
  };

  const mapHistories = (points: any[]): TAppPointHistory[] =>
    points.map((res) => ({
      status: getStatus(res.status, res.cancel_flg),
      pointName: res.point_name,
      point: res.point,
      entryDate: res.entry_date,
      actionMonth: res.action_month,
      cancelFlg: res.cancel_flg,
      memberMemo: res.member_memo,
    }));

  // 初回ロード・年変更時にリセットして先頭を取得
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setPointHistories([]);
    setTotalCount(0);
    setCurrentOffset(0);

    fetchPointHistories(year, 0, POINT_HISTORY_LIMIT)
      .then((res: TApiResult & TApiPointHisotriesResult) => {
        if (!res.isSuccess || !res.result) {
          console.error("fetchPointHistories failed:", res);
          setHasError(true);
          setIsLoading(false);
          return;
        }
        setPointHistories(mapHistories(res.result.point ?? []));
        setTotalCount(res.result.count ?? 0);
        setCurrentOffset(POINT_HISTORY_LIMIT);
        setRankSummary({
          name: res.result.rank.name,
          currentPeriodPoint: res.result.rank.current_period_point,
          icon: res.result.rank.icon,
        });
        setIsLoading(false);
      })
      .catch((e) => {
        console.error("PointHistory initial load exception:", e);
        setHasError(true);
        setIsLoading(false);
      });
  }, [year]);

  const stateRef = useRef({
    pointHistories,
    totalCount,
    isLoading,
    currentOffset,
    year,
  });
  stateRef.current = {
    pointHistories,
    totalCount,
    isLoading,
    currentOffset,
    year,
  };

  const loadMore = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const s = stateRef.current;
      if (!entries[0].isIntersecting) return;
      if (s.pointHistories.length >= s.totalCount) return;
      if (s.isLoading) return;

      setIsLoading(true);
      timerRef.current = setTimeout(() => {
        fetchPointHistories(s.year, s.currentOffset, POINT_HISTORY_LIMIT)
          .then((res: TApiResult & TApiPointHisotriesResult) => {
            if (!res.isSuccess || !res.result) {
              console.error("fetchPointHistories (loadMore) failed:", res);
              setHasError(true);
              setIsLoading(false);
              return;
            }
            setPointHistories((prev) => [
              ...prev,
              ...mapHistories(res.result!.point ?? []),
            ]);
            setCurrentOffset((prev) => prev + POINT_HISTORY_LIMIT);
            setIsLoading(false);
          })
          .catch((e) => {
            console.error("PointHistory loadMore exception:", e);
            setHasError(true);
            setIsLoading(false);
          });
      }, 500);
    },
    []
  );

  const observer = useMemo(
    () => new IntersectionObserver(loadMore, { rootMargin: "100px" }),
    [loadMore]
  );

  useEffect(() => {
    const target = document.getElementById("pointHistoryScrollTarget");
    if (target) observer.observe(target);
    return () => observer.disconnect();
  }, [observer]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const accessLogAction = site.isPoint ? ACCESS_LOG_ACTION.pointHistory : ACCESS_LOG_ACTION.rewardHistory;
    postMemberAccessLog(accessLogAction, document.referrer);
  }, []);

  return (
    <BaseHead
      title={routes.pointHistory.title}
      login={true}
      subdomain={subdomain}
    >
      <section className={STUtils.container_large} style={{minHeight: 'calc(100vh - 265px)'}} >
        <PointHistoryPointCard rank={rankSummary} />
        <PointHistoryList
          pointHistories={pointHistories}
          year={year}
          setYear={setYear}
          years={years}
          loading={isLoading}
          hasError={hasError}
        />
        <BottomNavigationBar />
      </section>
    </BaseHead>
  );
};

export default Presenter;
